import { Card } from "antd";
import styled from "styled-components";
import { cardSurface } from "src/components/DashboardCard";
import { colors } from "src/theme/colors";
import { radii, space, text } from "src/theme/ui";

/**
 * The frame around a TradingView widget.
 *
 * The widgets themselves are third-party iframes and are not ours to style,
 * which makes the frame the only part of this page we control — so it should
 * at least be the product's frame. It was a bare Ant Design Card with its
 * paddings knocked down: a different border, a different corner and no
 * shadow, sitting on a page whose neighbours in the sidebar are all built
 * from `cardSurface`.
 *
 * The body padding stays tight. A widget draws its own margins and its own
 * header, so the card's job here is to bound it, not to inset it twice.
 */
export const CardWidget = styled(Card)`
  ${cardSurface}

  /**
   * The head, on one line at one height.
   *
   * cardSurface writes its rules for the ProCard header class, and this is a
   * plain Ant Design Card — so every one of them was landing on a class that
   * is not in this markup, and the head kept the library's defaults.
   * Measured, the two heads on this page came out 44px and 42px, so two
   * cards side by side put their titles at two different heights; and with
   * the head laid out as a block rather than a row, a title and the control
   * opposite it aligned their boxes instead of their contents.
   *
   * The same 44px the rest of the product's cards use, so a card here opens
   * at the height a card anywhere else does.
   */
  .ant-card-head {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: ${space.md}px ${space.md}px 0;
    border-bottom: 0;
  }

  .ant-card-head-wrapper {
    width: 100%;
    align-items: center;
  }

  .ant-card-head-title {
    padding: 0;
  }

  .ant-card-body {
    padding: ${space.sm}px;
  }

  .ant-card-extra {
    padding: 0;
  }
`;

/* A card's name, in the product's own treatment rather than an antd
   Typography level — the widgets sat under a level-5 heading in one card and
   a level-3 in another, so two cards side by side named themselves at two
   different sizes. */
export const WidgetTitle = styled.h3`
  ${text.label}
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${colors.gray[900]};
`;

/**
 * Render a widget smaller than it wants to be.
 *
 * These embeds expose no font size and their markup is on another origin, so
 * the only way to bring their type and their logos down to the size of the
 * page around them is to render the whole widget smaller. A transform does
 * not change the layout box, so the width is divided back out to keep the
 * widget spanning its card. The browser rasterises the iframe at its scaled
 * size, so the text stays sharp rather than becoming a shrunken bitmap.
 *
 * Written once because three surfaces need it and they should agree: a page
 * with the tape at one scale, the news at another and the watchlist at full
 * size reads as three widgets from three places, which is what it was.
 */
export const scaled = (factor: number) => `
  > div:last-child {
    width: ${(100 / factor).toFixed(2)}%;
    transform: scale(${factor});
    transform-origin: left top;
  }
`;

/**
 * The ticker tape, with no card around it.
 *
 * A tape is a strip of moving text, not an object — putting a bordered,
 * shadowed card around one draws a box whose only content is a line of
 * prices sliding past, and the box's edges then cut them off mid-figure. The
 * same reasoning as the product's own returns tape, which also sits on the
 * page rather than in a card.
 *
 * And the same fade at both ends, for the same reason: a figure clipped by a
 * hard edge reads as a broken number, where one that dissolves reads as one
 * that has not arrived yet. The mask applies to the widget's iframe as it
 * would to any other content.
 */
export const TickerTape = styled.div`
  height: 38px;
  overflow: hidden;
  border-radius: ${radii.lg}px;
  background: ${colors.white};
  ${scaled(0.82)}

  /* The mask is on the element that carries the white, deliberately: it
     fades the background along with the prices, so the strip dissolves into
     the page at both ends rather than stopping at a hard white edge. */
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    #000 40px,
    #000 calc(100% - 40px),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 40px,
    #000 calc(100% - 40px),
    transparent
  );
`;

/* The news list, at the same scale as everything else on this page. Its
   height is what the scale leaves of the widget's own 350. */
export const NewsFrame = styled.div`
  position: relative;
  /* The widget is asked for enough height that this frame is full after the
     crop above — at its old height the last story ended 38px short of the
     bottom and the card finished on a band of blank paper. */
  height: 300px;
  overflow: hidden;
  ${scaled(0.89)}

  /**
   * TradingView's own header, cropped rather than covered.
   *
   * It paints a "Top Stories" heading inside its iframe that we can neither
   * restyle nor switch off, and the card already has a heading of its own.
   * A white band laid over it worked, but only as long as the card stayed
   * white — and once the card grew a real head, the band became 56px of
   * blank paper between the title and the first story. Pulling the widget up
   * past its own header removes it instead of hiding it, and leaves nothing
   * behind.
   */
  > div:last-child {
    margin-top: -50px;
  }
`;

/**
 * The quotes table, at the page's scale.
 *
 * It was the one surface still rendering at full size, which is why its type
 * read larger than everything around it — and, more usefully, why it was
 * scrolling sideways. The embed lays its columns out in its own CSS pixels;
 * dividing the width back out hands the iframe about twelve per cent MORE of
 * them, so the columns that were overflowing now fit, and the frozen-column
 * edge that overflow was drawing after "Name" has nothing left to announce.
 *
 * The table's columns are the embed's own and cannot be chosen from out
 * here; what we can do is give them the room to all be visible at once.
 */
export const ScreenerFrame = styled.div`
  /* Stated, because a transform does not change the layout box: left to its
     content the frame stands as tall as the UNSCALED widget and pushes the
     table's last rows out through the bottom of the card. This is what the
     scale leaves of the widget's own height. */
  height: 490px;
  overflow: hidden;
  ${scaled(0.89)}
`;

/* The watchlist, at the same scale as the news and the tape — it was the one
   surface still rendering at full size, which is why its tabs and range
   buttons read larger than anything around them. */
export const WatchlistFrame = styled.div<{ $height?: number }>`
  height: ${({ $height }) => $height ?? 550}px;
  overflow: hidden;
  ${scaled(0.89)}
`;

/* Any other embed on this page's sibling — the security details page — at
   the same scale, with the height the scale leaves of whatever the widget
   was asked for. A transform does not change the layout box, so the height
   has to be stated or the frame stands as tall as the unscaled widget and
   pushes everything below it down. */
export const WidgetFrame = styled.div<{
  $height?: number;
  $crop?: number;
  /**
   * How much smaller than life to render the embed.
   *
   * The page's own 0.89 by default — but a widget whose content is TEXT
   * rather than a chart should be left at 1. Shrinking a paragraph by eleven
   * per cent does not make the card tidier, it makes the paragraph harder to
   * read, and these embeds already set their body copy small.
   */
  $scale?: number;
}>`
  ${({ $height }) => ($height ? `height: ${$height}px;` : "")}
  overflow: hidden;
  ${({ $scale }) => scaled($scale ?? 0.89)}

  /* Some embeds paint a heading of their own — "AAPL Profile" over a card we
     have already titled "About the company". It cannot be restyled or
     switched off from out here, so it is pulled off the top rather than
     covered: a white band laid over it only works while the card is white,
     and leaves a strip of blank paper once it is not. Stated in the widget's
     own pixels, before the scale. */
  > div:last-child {
    ${({ $crop }) => ($crop ? `margin-top: -${$crop}px;` : "")}
  }
`;

/**
 * A control laid OVER an embed rather than in a head above it.
 *
 * The price card has no title — the widget names the company and prints the
 * price — so a head existed only to carry the chart's own Simple/Advanced
 * switch, and a head with one control in it reads as a card that has lost its
 * title and kept the gap. Cropping the widget's leading to close that gap was
 * worse: it cut the top off the company's name.
 *
 * These embeds leave their top-right corner empty, so the control goes there,
 * stacked on the widget. It carries its own white ground because what is
 * underneath it is a third-party canvas we cannot predict.
 */
export const WidgetStack = styled.div`
  position: relative;
`;

export const WidgetOverlay = styled.div<{ $top?: number }>`
  position: absolute;
  /* Below the advanced chart's own toolbar when there is one; against the
     card's padding when there is not. */
  top: ${({ $top }) => $top ?? 6}px;
  right: 6px;
  z-index: 2;
  padding: 3px;
  border-radius: ${radii.lg}px;
  background: ${colors.white};
`;
